/**
 * ============================================================
 * 本草数据（Chapter 1 医馆大院 · 药圃 4 味主药材）
 * ------------------------------------------------------------
 * 数据结构按完整库设计：后续章节可继续往 HERB_LIBRARY 里追加，
 * 无需改动任何系统代码（背包/图鉴/药圃采集均只依赖本接口）。
 *
 * 数据来源：
 *   - 性味归经 / 功效 / 用法：站内「本草图鉴」herbs-data.json（AI 整理）
 *   - 古籍简介：各药《神农本草经》《本草纲目》等公有领域典籍原文
 *   - 采集描述：游戏内文案，人工撰写
 * ============================================================
 */

/** 药材定义（接口即完整库的“表结构”） */
export interface Herb {
  /** 唯一标识（英文 id，用作资源 key） */
  id: string;
  /** 名称 */
  name: string;
  /** 拼音 */
  pinyin: string;
  /** 别名 */
  aliases: string[];
  /** 分类（如 补益药 / 清热药） */
  category: string;
  /** 四气（性）：寒 / 热 / 温 / 凉 / 平 */
  nature: string;
  /** 五味（味）：辛 / 甘 / 酸 / 苦 / 咸 … */
  flavor: string;
  /** 归经（经络列表） */
  meridians: string[];
  /** 功效（标签数组，图鉴与背包中展示） */
  effects: string[];
  /** 白话简介（一句话） */
  description: string;
  /** 古籍简介（背包中「古籍」栏） */
  classic: {
    /** 出处，如《神农本草经》 */
    source: string;
    /** 原文 */
    text: string;
    /** 白话注（可选，降低古文门槛） */
    note?: string;
  };
  /** 游戏内采集信息 */
  collect: {
    /** 药圃中采摘时的交互描述 */
    desc: string;
    /** 在药圃的方位（剧情/寻路提示） */
    spot: string;
  };
  /** 贴图 key（assets/sprites/herbs/<key>.png） */
  icon: string;
}

/** 完整药材库（后续章节追加条目即可） */
export const HERB_LIBRARY: Herb[] = [
  {
    id: 'gancao',
    name: '甘草',
    pinyin: 'Gān Cǎo',
    aliases: ['甜草根', '红甘草', '粉甘草', '国老'],
    category: '补益药',
    nature: '平',
    flavor: '甘',
    meridians: ['心', '肺', '脾', '胃'],
    effects: ['补脾益气', '清热解毒', '祛痰止咳', '缓急止痛', '调和诸药'],
    description:
      '甘草为豆科植物甘草的干燥根及根茎，味甜而得名，能调和诸药，被尊为「国老」。',
    classic: {
      source: '《神农本草经》',
      text: '主五脏六腑寒热邪气，坚筋骨，长肌肉，倍力，金疮肿，解毒。',
      note: '古人称它能平复五脏六腑的偏性，被尊为药中之「国老」。',
    },
    collect: {
      desc: '拨开青石板边的土，露出甘草粗壮的根——甜香扑鼻。',
      spot: '药圃西侧，靠墙的沙土畦里。',
    },
    icon: 'herb_gancao',
  },
  {
    id: 'danggui',
    name: '当归',
    pinyin: 'Dāng Guī',
    aliases: ['秦归', '云归', '西当归', '全当归'],
    category: '补益药',
    nature: '温',
    flavor: '甘、辛',
    meridians: ['肝', '心', '脾'],
    effects: ['补血活血', '调经止痛', '润肠通便'],
    description:
      '当归为伞形科植物当归的干燥根，主产甘肃岷县。归头、归身、归尾各有分工，是补血第一药。',
    classic: {
      source: '《神农本草经》',
      text: '主咳逆上气，温疟寒热洗洗在皮肤中，妇人漏下，绝子，诸恶疮疡金疮，煮饮之。',
      note: '「当归」之名，取「使气血各有所归」之意。',
    },
    collect: {
      desc: '当归的茎叶丛生，轻轻一拔带出红棕色的根，辛香浓郁。',
      spot: '药圃东侧，半阴的畦垄之间。',
    },
    icon: 'herb_danggui',
  },
  {
    id: 'jinyinhua',
    name: '金银花',
    pinyin: 'Jīn Yín Huā',
    aliases: ['忍冬花', '双花', '二宝花', '银花'],
    category: '清热药',
    nature: '寒',
    flavor: '甘',
    meridians: ['肺', '心', '胃'],
    effects: ['清热解毒', '疏散风热', '凉血止痢'],
    description:
      '金银花为忍冬科植物忍冬的干燥花蕾，花开先白后黄，黄白相映，故名「金银花」。',
    classic: {
      source: '《本草纲目》',
      text: '忍冬，茎叶及花，功用皆同……一切风湿气，及诸肿毒，痈疽疥癣，散热解毒。',
      note: '李时珍称其为治疮要药，四季常青，又名「忍冬」。',
    },
    collect: {
      desc: '藤蔓攀在药圃篱笆上，摘下黄白相间的花蕾，清香微苦。',
      spot: '药圃南边，篱笆攀藤处。',
    },
    icon: 'herb_jinyinhua',
  },
  {
    id: 'lingzhi',
    name: '灵芝',
    pinyin: 'Líng Zhī',
    aliases: ['灵芝草', '瑞草', '仙草', '赤芝'],
    category: '补益药',
    nature: '平',
    flavor: '甘',
    meridians: ['心', '肺', '肝', '肾'],
    effects: ['补气安神', '止咳平喘', '益精强志', '延年益寿'],
    description:
      '灵芝为多孔菌科真菌的干燥子实体，菌盖半圆形，红褐带漆光，自古视为「仙草」。',
    classic: {
      source: '《神农本草经》',
      text: '主胸中结，益心气，补中，增慧智，不忘。久食轻身不老，延年神仙。',
      note: '列为上品，古人认为久服可轻身延年。',
    },
    collect: {
      desc: '老槐树根下的枯木上，一朵赤芝撑着油亮的伞盖。',
      spot: '药圃北角，老槐树根旁。',
    },
    icon: 'herb_lingzhi',
  },
];

/** 按 id 快速查找 */
export const HERB_MAP: Record<string, Herb> = Object.fromEntries(
  HERB_LIBRARY.map((h) => [h.id, h]),
);

/** 主线任务需要：甘草 ×3 */
export const QUEST_HERB_ID = 'gancao';
export const QUEST_HERB_COUNT = 3;
